import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { compositeProductService } from '@/services/compositeProductService';

/**
 * Formulário para criação de um novo produto composto (apenas o master).
 * Após a criação, o usuário é redirecionado para a listagem onde pode
 * adicionar os produtos filhos via edição inline.
 */
export const CompositeProductFormView: React.FC = () => {
  const navigate = useNavigate();

  const [skuMaster, setSkuMaster] = useState('');
  const [description, setDescription] = useState('');

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ skuMaster?: string; description?: string; general?: string }>({});

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!skuMaster.trim()) {
      newErrors.skuMaster = 'SKU Master é obrigatório';
    } else if (skuMaster.trim().length > 10) {
      newErrors.skuMaster = 'SKU Master deve ter no máximo 10 caracteres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setErrors({});

    try {
      await compositeProductService.createCompositeProduct({
        skuMaster: skuMaster.trim().toUpperCase(),
        description: description.trim() || undefined,
      });
      navigate('/composite-products');
    } catch (err) {
      setErrors({ general: err instanceof Error ? err.message : 'Erro ao criar produto composto' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      {/* Cabeçalho */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/composite-products')}
          className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Novo Produto Composto</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Crie o produto mestre e adicione os itens filhos na listagem
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title="Dados do Produto Composto" />
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Erro geral */}
            {errors.general && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                <AlertCircle size={16} />
                {errors.general}
              </div>
            )}

            {/* SKU Master */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                SKU Master <span className="text-red-500">*</span>
              </label>
              <Input
                value={skuMaster}
                onChange={e => setSkuMaster(e.target.value.toUpperCase())}
                placeholder="Ex: KIT001"
                maxLength={10}
                className={`font-mono uppercase ${errors.skuMaster ? 'border-red-400 focus:ring-red-400' : ''}`}
              />
              {errors.skuMaster && (
                <p className="text-red-500 text-xs mt-1">{errors.skuMaster}</p>
              )}
              <p className="text-slate-400 text-xs mt-1">
                Código único de até 10 caracteres para identificar este produto composto
              </p>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descrição
              </label>
              <Input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Ex: Kit Organização Básico"
                maxLength={150}
              />
              {errors.description && (
                <p className="text-red-500 text-xs mt-1">{errors.description}</p>
              )}
            </div>

            {/* Ações */}
            <div className="flex items-center gap-3 pt-2">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
                className="flex items-center gap-2"
              >
                {saving ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {saving ? 'Salvando...' : 'Salvar Produto Composto'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/composite-products')}
                disabled={saving}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
        <p className="font-medium mb-1">Próximos passos</p>
        <p>
          Após criar o produto composto, você será redirecionado para a listagem onde poderá
          expandir o produto e adicionar os itens filhos com suas respectivas quantidades,
          preços promocionais e demais configurações.
        </p>
      </div>
    </div>
  );
};
